import {
  IPlanV3,
  StripePricesLookupKeys,
  SubscriptionStatus,
  allowedSubscriptionStatus,
  currencyFormatter,
  findPlanByLookupKey,
} from "shared";
import {
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { useParams } from "react-router";
import { Tooltip } from "@mui/material";

import { Card } from "../../../common/Atoms/Card";
import { usePlans } from "../../hooks/usePlans";
import { Select } from "../../../common/Atoms/Select";
import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { Button } from "../../../common/Atoms/Button";
import { createCheckout } from "../../lib/api";
import { events, logAnalyticEvent } from "../../lib/analytics";
import { useUser } from "../../hooks/useUser";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { useChangePlanMutation } from "../../services/api/subscriptionApi/subscription";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";

import { PlanItem } from "./PlanItem";
import { PlanChangeModal } from "./PlanChangeModal";

interface INewPlanProps {
  plan: IPlanV3;
  selectedCurrency: string;
}

const initPlanChangeModal = {
  currentPlan: null,
  newPlan: null,
  show: false,
};

export function NewPlan({
  plan,
  selectedCurrency,
}: INewPlanProps) {
  const { data: user } = useUser();
  const id = Number(useParams().subscriptionId);
  const { data: subscriptions } = useSubscriptions();
  const { data: plans } = usePlans();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const [loading, setLoading] = useState(false);
  const [chosenPrice, setChosenPrice] = useState<StripePricesLookupKeys>(null);
  const [ changePlan, { isLoading: changePlanLoading } ] = useChangePlanMutation();
  const [ planChangeModal, setPlanChangeModal ] = useState(initPlanChangeModal);
  const featureFlags = useFeatureFlags();

  const subscription = useMemo(() => {
    return subscriptions.find(s => s.id === id);
  }, [ subscriptions]);

  useEffect(() => {
    if (!chosenPrice && plans?.length && subscription) {
      if (allowedSubscriptionStatus.includes(subscription.status)) {
        const currentLookup = subscription.mainLookupKey as StripePricesLookupKeys;
        const planConfig = findPlanByLookupKey(currentLookup);

        if (plans.find(p => p.lookup_key === currentLookup) && planConfig.name === plan.name) {
          setChosenPrice(subscription.mainLookupKey as StripePricesLookupKeys);

          return;
        }
      }
      const defaultPrice = Object.keys(plan.prices).find(k => plan.prices[k].default);

      setChosenPrice(defaultPrice as StripePricesLookupKeys);
    }

  }, [chosenPrice, plans, subscription]);

  const priceOptions = useMemo(() => {
    if (!plans) return [];

    const keysInPlan = Object.keys(plan.prices) as StripePricesLookupKeys[];

    return plans
      .filter(p => keysInPlan.includes(p.lookup_key as StripePricesLookupKeys))
      .sort((a, b) => (
        plan.prices[a.lookup_key as StripePricesLookupKeys].limits.invoices - plan.prices[b.lookup_key as StripePricesLookupKeys].limits.invoices
      ))
      .map(p => {
        return {
          label: `${plan.prices[p.lookup_key as StripePricesLookupKeys].limits.invoices} invoices & ${plan.prices[p.lookup_key as StripePricesLookupKeys].limits.emails} emails / month`,
          value: p.lookup_key,
        };
      });
  }, [plan, plans]);

  const chosenPriceDetails = useMemo(() => {
    if (!chosenPrice || !plans) return null;

    const currentPrice = plan.prices[chosenPrice];
    const baseStripePrice = plans.find(p => p.lookup_key === chosenPrice);
    const opt = baseStripePrice?.currency_options?.[selectedCurrency.toLowerCase()];
    const basePrice = opt?.unit_amount ?? baseStripePrice?.unit_amount;

    if (basePrice == null) return null;

    const basePriceFormatted = currencyFormatter(selectedCurrency)(basePrice / 100);

    return {
      planDetails: currentPrice,
      planCost: plans.find(p => p.lookup_key === chosenPrice),
      basePriceFormatted,
    };
  }, [chosenPrice, plan, plans, selectedCurrency]);

  const items = useMemo(() => {
    if (!chosenPriceDetails?.planDetails) return [];

    const formatCurrency = currencyFormatter(selectedCurrency);
    const getUnitAmountFromPlans = (lookupKeys: StripePricesLookupKeys[]) => {
      for (const lookupKey of lookupKeys) {
        const stripePrice = plans?.find(p => p.lookup_key === lookupKey);
        const selectedOption = stripePrice?.currency_options?.[selectedCurrency.toLowerCase()];
        const unitAmount = selectedOption?.unit_amount ?? stripePrice?.unit_amount;

        if (unitAmount != null) {
          return unitAmount;
        }
      }

      return null;
    };
    const smsUnitAmount = getUnitAmountFromPlans([ StripePricesLookupKeys.SmsManuallyManaged ]);
    const orgShareUnitAmount = getUnitAmountFromPlans([
      StripePricesLookupKeys.OrgAddOnMultiCurrency2026,
      StripePricesLookupKeys.OrgAddOn29,
      StripePricesLookupKeys.OrgAddOn,
    ]);
    const smsReminderPrice = formatCurrency((smsUnitAmount ?? 10) / 100);
    const sharedAllowancePrice = formatCurrency((orgShareUnitAmount ?? 2900) / 100);

    const base = [
      {
        icon: CheckCircleIcon,
        text: `${ chosenPriceDetails.planDetails.limits.invoices } Invoices* per Month`,
      },
      {
        icon: CheckCircleIcon,
        text: `${ chosenPriceDetails.planDetails.limits.users } Team Members`,
      },
      {
        icon: CheckCircleIcon,
        text: `Issue Late Fees & Interest on Invoices`,
      },
      {
        icon: CheckCircleIcon,
        text: `Auto Send Statements`,
      },
      {
        icon: CheckCircleIcon,
        text: `Prompt Payment Discounts`,
      },
      {
        icon: CheckCircleIcon,
        text: `Email Reminders`,
      },
      {
        icon: CheckCircleIcon,
        text: `SMS Reminders (${smsReminderPrice} each)`,
      },
      {
        icon: CheckCircleIcon,
        text: `1 Phone Number Included`,
      },
    ];

    if (plan.features.statementInterest) {
      base.push({
        icon: CheckCircleIcon,
        text: `Charge Interest on Statements`,
      });
    }

    if (plan.features.customDomain) {
      base.push({
        icon: CheckCircleIcon,
        text: `BYO Email Domain`,
      });
    }

    if (plan.features.paymentPlans) {
      // Also check feature flag for now
      if (featureFlags?.paymentPlans) {
        base.push({
          icon: CheckCircleIcon,
          text: `Payment Plans for Customers`,
        });
      }
    }

    if (plan.features.multicurrency) {
      base.push({
        icon: CheckCircleIcon,
        text: `Multicurrency`,
      });
    }

    if (plan.features.webhooks) {
      base.push({
        icon: CheckCircleIcon,
        text: `Webhooks`,
      });
    }

    if (plan.features.multiEntityReporting) {
      base.push({
        icon: CheckCircleIcon,
        text: `Multi-entity Reporting`,
      });
    }

    if (plan.features.userPermissions) {
      base.push({
        icon: CheckCircleIcon,
        text: `Team Member Permissions`,
      });
    }

    if (plan.name === `Pro`) {
      base.push({
        icon: CheckCircleIcon,
        text: `Share allowance with other organisations (${sharedAllowancePrice}ea / Month)`,
      });
    }

    return base;
  }, [ plan, chosenPriceDetails, selectedCurrency, plans ]);

  async function onCheckout(changePlan?: boolean) {
    setLoading(true);
    try {
      const res = await createCheckout(chosenPrice, subscriptionId, selectedCurrency, changePlan);

      logAnalyticEvent(events.beginCheckout, {
        userId: user.id,
        stripeProduct: chosenPrice,
      });

      window.location.href = res.data.url;
    }
    catch (err) {
      toast.error(err?.message || `Could not create checkout session.`);
    }
    finally {
      setLoading(false);
    }
  }

  const hasCurrentPlan = [...allowedSubscriptionStatus, SubscriptionStatus.PAST_DUE, SubscriptionStatus.UNPAID].includes(subscription?.status);
  const canChangePlan = allowedSubscriptionStatus.includes(subscription?.status);
  const isCurrentPlan = subscription?.mainLookupKey === chosenPrice;

  function onChangePlan() {
    setPlanChangeModal({
      show: true,
      currentPlan: subscription?.mainLookupKey,
      newPlan: chosenPrice,
    });
  }

  async function onChangePlanConfirm() {
    await changePlan({
      lookupKey: chosenPrice,
      subscriptionId,
    });

    setPlanChangeModal(initPlanChangeModal);
  }

  function renderButton() {
    if (hasCurrentPlan && isCurrentPlan) {
      return (
        <Button
          onClick={ () => {} }
          size={ `2xl` }
          disabled
        >
          { `Current Plan` }
        </Button>
      );
    }

    if (hasCurrentPlan) {
      if (!canChangePlan) {
        return (
          <Tooltip title={ `Update your payment method to change plans.` }>
            <Button
              onClick={ () => {} }
              size={ `2xl` }
              disabled
            >
              { `Cannot Change Plan` }
            </Button>
          </Tooltip>
        );
      }

      return (
        <Button
          onClick={ () => onChangePlan() }
          loading={ changePlanLoading }
          disabled={ changePlanLoading }
          size={ `2xl` }
        >
          { `Change Plan` }
        </Button>
      );
    }

    return (
      <Button
        onClick={ () => onCheckout() }
        loading={ loading }
        size={ `2xl` }
      >
        { `Choose Plan` }
      </Button>
    );
  }

  const isPro = plan.name === `Pro`;
  const isAnnual = chosenPriceDetails?.planDetails?.annual;

  return (
    <>
      <div className={ `min-h-full` }>
        <Card
          className={ `${isPro ? `ring-2 ring-paidniceGreen-400` : `ring-1 ring-gray-200`} rounded-2xl bg-white p-6 shadow-xs hover:shadow-md transition flex flex-col justify-between min-h-full` }
        >
          <div>
            <div
              id={ `plan-${plan.name}-header` }
              className={ `min-h-20` }
            >
              <h2 className={ `text-secondary text-2xl font-semibold` }>
                { plan.name }
              </h2>
              <Paragraph
                variant={ `secondary` }
                className={ `mt-2` }
              >
                { plan.description }
              </Paragraph>
            </div>
            <div className={ `flex flex-col` }>
              <div className={ `mb-4` }>
                <p className={ `flex items-baseline` }>
                  <span className={ `text-4xl font-bold tracking-tight text-gray-900` }>
                    { chosenPriceDetails?.basePriceFormatted }
                  </span>
                  <span className={ `ml-2 text-gray-600` }>
                    { isAnnual ? `/ year` : ` / month` }
                  </span>
                </p>
              </div>
              <Select
                options={ priceOptions }
                label={ `` }
                onChange={ e => setChosenPrice(e.value) }
                selected={ chosenPrice }
                className={ `mb-6 w-full` }
              />

              <div className={ `space-y-4 mt-4` }>
                {
                  items.map((item, i) => (
                    <PlanItem key={ i }
                      item={ item } />
                  ))
                }
              </div>
            </div>
          </div>
          <div className={ `flex justify-center pt-10` }>
            { renderButton() }
          </div>
        </Card>
      </div>
      <PlanChangeModal
        onConfirm={ onChangePlanConfirm }
        onCancel={ () => setPlanChangeModal(initPlanChangeModal) }
        show={ planChangeModal.show }
        currentPlan={ planChangeModal.currentPlan }
        newPlan={ planChangeModal.newPlan }
        trial={ subscription?.status === SubscriptionStatus.TRIALING }
        loading={ changePlanLoading }
      />
    </>
  );
}
